import ConnectedAccount from 'components/ConnectedAccount';
import ManageConnectionModal from 'components/modals/ManageConnectionModal';
import { useIsPendoEnabled } from 'hooks/useIsPendoEnabled';
import ManageConnectionDropdown from 'pages/IntegrationsPage/Integrations/ManageConnectionDropdown';
import React from 'react';
import {
  ConnectedAccountList,
  RawConnectedAccount,
} from 'records/ConnectedAccount';
import { ModalState } from 'records/ModalState';
import { truncateString } from 'utils/general';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';

type Props = {
  accounts: ConnectedAccountList;
} & WithAppStoreProps;

const createdIntegrationConnectionEventNames = new Map(
  Object.entries({
    googledrive: 'Created integration connection - Google Drive',
    egnyte: 'Created integration connection - Egnyte',
    box: 'Created integration connection - Box',
    onedrive: 'Created integration connection - OneDrive',
    sharepoint: 'Created integration connection - SharePoint',
    dropbox: 'Created integration connection - Dropbox',
  })
);

const ConnectedAccounts: React.FC<Props> = ({
  accounts,
  actions,
  appStore,
}: Props) => {
  const { isPendoEnabled } = useIsPendoEnabled();
  const { selectedProject } = appStore.state;
  const handleAccountConnectionClick = (
    accountLabel: string,
    isExpired: boolean,
    providerType: string,
    connectionId: string,
    newConnection: boolean
  ): void => {
    if (isPendoEnabled) {
      if (newConnection) {
        const eventName =
          createdIntegrationConnectionEventNames.get(providerType);

        if (eventName) {
          pendo.track(eventName);
        } else {
          pendo.track('Created integration connection - Other', {
            providerType,
          });
        }
      } else {
        pendo.track('Manage connection initiated');
      }
    }

    const modalState = new ModalState({
      id: 'launchIntegrationsModal',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        accountLabel,
        isExpired,
        newConnection,
        providerType,
        connectionId,
      },
      component: ManageConnectionModal,
      shouldCloseOnEsc: false,
      shouldCloseOnOverlayClick: false,
    });

    actions.createModal(modalState);
  };

  const { translate } = useTranslator(['integrationsPage']);
  const newConnectionProvider = localStorage.getItem('started-connection');

  if (newConnectionProvider) {
    localStorage.removeItem('started-connection');
    const filteredAccount = accounts
      .filter(
        (ca) =>
          ca.providerType === newConnectionProvider &&
          Date.now() - Date.parse(ca.updatedAt) < 300000
      )
      .first() as RawConnectedAccount;
    if (typeof filteredAccount !== 'undefined') {
      const { label, isExpired, providerType, id } = filteredAccount;
      handleAccountConnectionClick(label, isExpired, providerType, id, true);
    }
  }

  if (!accounts.size) {
    return (
      <div className="color-gray-7 font-size-5 m3">
        {translate('integrationsPage:noIntegrations')}
      </div>
    );
  }
  return (
    <>
      {accounts.map((connectedAccount, index) => {
        return (
          <ConnectedAccount
            key={connectedAccount.id}
            account={connectedAccount}
            index={index}
            label={truncateString(connectedAccount.label, 23)}
            onClick={() =>
              handleAccountConnectionClick(
                connectedAccount.label,
                connectedAccount.isExpired,
                connectedAccount.providerType,
                connectedAccount.id,
                false
              )
            }
          >
            <ManageConnectionDropdown
              projectId={selectedProject}
              providerType={connectedAccount.providerType}
              connectionId={connectedAccount.id}
              accountLabel={connectedAccount.label}
              isExpired={connectedAccount.isExpired}
            />
          </ConnectedAccount>
        );
      })}
    </>
  );
};

export default withAppStore<Props>(ConnectedAccounts);
